// ABOUTME: Pure computation utilities for price statistics and comparisons.
// ABOUTME: Extracted from api.ts — no API calls, only local calculations.

import type { Competitor, PriceStatistics } from '@/types';

/**
 * Calculate price statistics from competitors
 */
export function calculatePriceStatistics(
    userPrice: number,
    competitors: Competitor[]
): PriceStatistics {
    const allPrices = competitors.flatMap((c) =>
        c.prices.map((p) => ({
            price: p.price,
            product: c.product.name,
            brand: c.product.brand,
            platform: p.platform,
        }))
    );

    if (allPrices.length === 0) {
        return {
            userPrice,
            competitorAvg: 0,
            lowestPrice: 0,
            lowestPriceProduct: '-',
            lowestPricePlatform: 'blinkit',
            highestPrice: 0,
            highestPriceProduct: '-',
            highestPricePlatform: 'blinkit',
            priceSpread: 0,
            userPosition: 1,
            totalCompetitors: 0,
            recommendation: 'No competitors found',
        };
    }

    const prices = allPrices.map((p) => p.price);
    const avg = Math.round(prices.reduce((a, b) => a + b, 0) / prices.length);

    const sorted = [...allPrices].sort((a, b) => a.price - b.price);
    const lowest = sorted[0];
    const highest = sorted[sorted.length - 1];

    // Calculate user position (1 = cheapest)
    const allWithUser = [...prices, userPrice].sort((a, b) => a - b);
    const position = allWithUser.indexOf(userPrice) + 1;

    // Generate recommendation
    let recommendation = '';
    const delta = userPrice - avg;

    if (delta < -10) {
        recommendation =
            "You're the CHEAPEST. Watch margin erosion and maintain quality.";
    } else if (delta < 0) {
        recommendation =
            "You're COMPETITIVELY PRICED. Maintain current pricing.";
    } else if (delta === 0) {
        recommendation =
            "You're AT MARKET PRICE. Differentiate via reviews or features.";
    } else if (delta <= 10) {
        recommendation =
            "You're SLIGHTLY ABOVE market. Monitor competitor moves.";
    } else {
        recommendation =
            "You're PREMIUM priced. Ensure quality/brand justifies it.";
    }

    return {
        userPrice,
        competitorAvg: avg,
        lowestPrice: lowest.price,
        lowestPriceProduct: `${lowest.brand}`,
        lowestPricePlatform: lowest.platform,
        highestPrice: highest.price,
        highestPriceProduct: `${highest.brand}`,
        highestPricePlatform: highest.platform,
        priceSpread: highest.price - lowest.price,
        userPosition: position,
        totalCompetitors: competitors.length,
        recommendation,
    };
}
